/**
 * Allocation-free scalar and vector maths for the three-free subsystems
 * (combat, physics, input, ai, audio). See ARCHITECTURE.md §4.1.
 *
 * Vectors are plain `{ x, y, z }` objects. Every operation writes into an explicit
 * destination so hot paths never allocate.
 */

import { EPSILON } from './constants.js';

export const PI = Math.PI;
export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

export function clamp(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function inverseLerp(a, b, v) {
  return Math.abs(b - a) < EPSILON ? 0 : (v - a) / (b - a);
}

/** Maps `v` from [inMin,inMax] to [outMin,outMax] without clamping. */
export function remap(v, inMin, inMax, outMin, outMax) {
  return outMin + (outMax - outMin) * inverseLerp(inMin, inMax, v);
}

export function smoothstep(edge0, edge1, v) {
  const t = clamp01(inverseLerp(edge0, edge1, v));
  return t * t * (3 - 2 * t);
}

export function smootherstep(edge0, edge1, v) {
  const t = clamp01(inverseLerp(edge0, edge1, v));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Moves `current` toward `target` by at most `maxDelta`. */
export function approach(current, target, maxDelta) {
  if (current < target) return Math.min(current + maxDelta, target);
  if (current > target) return Math.max(current - maxDelta, target);
  return target;
}

/**
 * Frame-rate independent exponential smoothing.
 * `rate` is the fraction of the remaining distance covered per second.
 */
export function damp(current, target, rate, dt) {
  return target + (current - target) * Math.exp(-rate * dt);
}

export function sign(v) {
  return v < 0 ? -1 : v > 0 ? 1 : 0;
}

/** Sign that never returns 0 — useful for facing values. */
export function signNonZero(v) {
  return v < 0 ? -1 : 1;
}

/**
 * Wraps an angle to the half-open interval (-PI, PI].
 *
 * The upper bound is inclusive and the lower bound is not, so -PI is normalised to +PI. That
 * matters because facing yaw is compared against +PI/2 and -PI/2: a value that silently became
 * -PI when the contract promised +PI would make a shortest-arc comparison pick the wrong
 * direction exactly at the turnaround point.
 */
export function wrapPi(a) {
  let x = (a + PI) % TAU;
  if (x <= 0) x += TAU;
  return x - PI;
}

/** Shortest-arc angle interpolation. */
export function lerpAngle(a, b, t) {
  return a + wrapPi(b - a) * t;
}

/** Shortest-arc angular distance, signed. */
export function angleDelta(a, b) {
  return wrapPi(b - a);
}

export function moveTowardAngle(current, target, maxDelta) {
  const d = angleDelta(current, target);
  if (Math.abs(d) <= maxDelta) return wrapPi(target);
  return wrapPi(current + Math.sign(d) * maxDelta);
}

/** Triangle wave in [0,1] with period 1. Useful for breathing/idle without trig cost. */
export function triangle01(t) {
  const f = t - Math.floor(t);
  return f < 0.5 ? f * 2 : 2 - f * 2;
}

/** Smooth 0..1..0 pulse over [0,1]; C1 continuous at the ends. */
export function pulse01(t) {
  const f = clamp01(t);
  return Math.sin(f * PI);
}

/** Ease used for anticipation: slow out, fast in. */
export function easeInCubic(t) {
  const x = clamp01(t);
  return x * x * x;
}

export function easeOutCubic(t) {
  const x = clamp01(t) - 1;
  return x * x * x + 1;
}

export function easeOutQuint(t) {
  const x = clamp01(t) - 1;
  return 1 + x * x * x * x * x;
}

export function easeInOutCubic(t) {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/** Overshoot ease for snappy follow-through. `s` controls overshoot amount. */
export function easeOutBack(t, s = 1.7) {
  const x = clamp01(t) - 1;
  return 1 + (s + 1) * x * x * x + s * x * x;
}

/** Decaying oscillation, settles at 1. Used for impact recoil. */
export function springSettle(t, freq = 9, decay = 7) {
  const x = clamp01(t);
  return 1 - Math.cos(x * freq) * Math.exp(-decay * x);
}

export function nearlyEqual(a, b, eps = EPSILON) {
  return Math.abs(a - b) <= eps;
}

// ---------------------------------------------------------------------------
// Vectors — plain {x,y,z}
// ---------------------------------------------------------------------------

export function vec3(x = 0, y = 0, z = 0) {
  return { x, y, z };
}

export function vset(out, x, y, z) {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
}

export function vcopy(out, a) {
  out.x = a.x;
  out.y = a.y;
  out.z = a.z;
  return out;
}

export function vzero(out) {
  out.x = 0;
  out.y = 0;
  out.z = 0;
  return out;
}

export function vadd(out, a, b) {
  out.x = a.x + b.x;
  out.y = a.y + b.y;
  out.z = a.z + b.z;
  return out;
}

export function vsub(out, a, b) {
  out.x = a.x - b.x;
  out.y = a.y - b.y;
  out.z = a.z - b.z;
  return out;
}

export function vscale(out, a, s) {
  out.x = a.x * s;
  out.y = a.y * s;
  out.z = a.z * s;
  return out;
}

/** out = a + b * s */
export function vaddScaled(out, a, b, s) {
  out.x = a.x + b.x * s;
  out.y = a.y + b.y * s;
  out.z = a.z + b.z * s;
  return out;
}

export function vlerp(out, a, b, t) {
  out.x = a.x + (b.x - a.x) * t;
  out.y = a.y + (b.y - a.y) * t;
  out.z = a.z + (b.z - a.z) * t;
  return out;
}

export function vdot(a, b) {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function vcross(out, a, b) {
  const ax = a.x;
  const ay = a.y;
  const az = a.z;
  const bx = b.x;
  const by = b.y;
  const bz = b.z;
  out.x = ay * bz - az * by;
  out.y = az * bx - ax * bz;
  out.z = ax * by - ay * bx;
  return out;
}

export function vlenSq(a) {
  return a.x * a.x + a.y * a.y + a.z * a.z;
}

export function vlen(a) {
  return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}

export function vdistSq(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}

export function vdist(a, b) {
  return Math.sqrt(vdistSq(a, b));
}

export function vnormalize(out, a) {
  const l = vlen(a);
  if (l < EPSILON) return vzero(out);
  return vscale(out, a, 1 / l);
}

/** Clamps the magnitude of `a` to `max`, writing into `out`. */
export function vclampLength(out, a, max) {
  const lsq = vlenSq(a);
  if (lsq <= max * max || lsq < EPSILON) return vcopy(out, a);
  return vscale(out, a, max / Math.sqrt(lsq));
}

// ---------------------------------------------------------------------------
// Capsule / box helpers used by physics and hit detection
// ---------------------------------------------------------------------------

/**
 * Closest point on segment [a,b] to point p. Writes into `out`, returns the
 * parametric position t in [0,1].
 */
export function closestPointOnSegment(out, a, b, p) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abz = b.z - a.z;
  const denom = abx * abx + aby * aby + abz * abz;
  let t = 0;
  if (denom > EPSILON) {
    t = ((p.x - a.x) * abx + (p.y - a.y) * aby + (p.z - a.z) * abz) / denom;
    t = clamp01(t);
  }
  out.x = a.x + abx * t;
  out.y = a.y + aby * t;
  out.z = a.z + abz * t;
  return t;
}

/**
 * Squared distance between segments [p0,p1] and [q0,q1]. Allocation free but
 * needs two scratch vectors from the caller.
 */
export function segmentSegmentDistSq(p0, p1, q0, q1, scratchA, scratchB) {
  // Iterative refinement is enough for our short capsules and is branch-light.
  closestPointOnSegment(scratchA, p0, p1, q0);
  closestPointOnSegment(scratchB, q0, q1, scratchA);
  closestPointOnSegment(scratchA, p0, p1, scratchB);
  closestPointOnSegment(scratchB, q0, q1, scratchA);
  return vdistSq(scratchA, scratchB);
}

/** Do two axis-aligned boxes overlap? Boxes are {minX,maxX,minY,maxY,minZ,maxZ}. */
export function aabbOverlap(a, b) {
  return (
    a.minX <= b.maxX &&
    a.maxX >= b.minX &&
    a.minY <= b.maxY &&
    a.maxY >= b.minY &&
    a.minZ <= b.maxZ &&
    a.maxZ >= b.minZ
  );
}

/** Overlap depth along X only — used for pushbox separation on the combat axis. */
export function aabbOverlapX(a, b) {
  return Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
}

// ---------------------------------------------------------------------------
// Hashing — deterministic, used for replay checksums and stable ids
// ---------------------------------------------------------------------------

export function fnv1a32(str, seed = 0x811c9dc5) {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Folds a float into a 32-bit digest deterministically (quantised to 1e-4). */
export function hashFloat(h, v) {
  const q = Math.round(v * 10000) | 0;
  let x = h >>> 0;
  x ^= q & 0xff;
  x = Math.imul(x, 0x01000193) >>> 0;
  x ^= (q >>> 8) & 0xff;
  x = Math.imul(x, 0x01000193) >>> 0;
  x ^= (q >>> 16) & 0xff;
  x = Math.imul(x, 0x01000193) >>> 0;
  x ^= (q >>> 24) & 0xff;
  x = Math.imul(x, 0x01000193) >>> 0;
  return x >>> 0;
}

export function hashInt(h, v) {
  let x = h >>> 0;
  const q = v | 0;
  x ^= q & 0xffff;
  x = Math.imul(x, 0x01000193) >>> 0;
  x ^= (q >>> 16) & 0xffff;
  x = Math.imul(x, 0x01000193) >>> 0;
  return x >>> 0;
}

export function hashString(h, str) {
  return fnv1a32(str, h);
}

// ---------------------------------------------------------------------------
// Frame-data conversions (ARCHITECTURE.md §7.5)
// ---------------------------------------------------------------------------

/** f60 frames -> simulation ticks. */
export function f60(frames) {
  return Math.round(frames * 2);
}

/** simulation ticks -> f60 frames (may be fractional for display). */
export function toF60(ticks) {
  return ticks / 2;
}
